<script setup>
import {computed, reactive, watch} from "vue"

import CounterNumberValue from "@shared/components/atoms/CounterNumberValue.vue"
import AlertModal from "@shared/components/modals/AlertModal.vue"
import {useTournamentStore} from "@shared/stores/TournamentStore"

const props = defineProps({
    gameId: {
        type: Number,
        required: true
    }
})

const tournamentStore = useTournamentStore()

const progress = computed(() => tournamentStore.getTournamentProgress)

const state = reactive({
    remainingTime: progress.value.timeRemaining,
    isAlertVisible: false,
    isJoiningTournamentConfirmationVisible: false,
    isEnded: false,
    isWarning: false,
    timerInterval: null,
})

const startCountdown = () => {
    if(state.timerInterval) {
        clearInterval(state.timerInterval)
    }

    state.timerInterval = setInterval(updateTimer, 1000)
}

const formatSecondsToCountdownTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
}

const updateTimer = () => {
    if (state.remainingTime > 0) {
        state.isWarning = state.remainingTime < 60

        state.remainingTime -= 1

        if (state.remainingTime <= 0) {
            state.isEnded = true
        }
    } else {
        clearInterval(state.timerInterval)

        state.isWarning = true
        if(tournamentStore.tournament.buy_in_duration) {
            state.isAlertVisible = true
        }
    }
}

const handleTournamentBarClick = () => {
    if(progress.value.started) {
        tournamentStore.setTournamentModalVisibility(true)
    }
}

const handleStartTournament = () => {
    tournamentStore
        .fetchStartTournament(props.gameId)
        .then(() => {
            state.remainingTime = progress.value.timeRemaining
            tournamentStore.watchTournamentProgress(tournamentStore.tournament.id)
            startCountdown()
        })
}

const handleRebuyClick = () => {
    state.isAlertVisible = false
    state.isJoiningTournamentConfirmationVisible = true
}

const handleJoinTournament = () => {
    tournamentStore.fetchJoinTournament().then(() => {
        state.isJoiningTournamentConfirmationVisible = false
    })
}

const handlePayoutClick = () => {
    tournamentStore.fetchTournament(tournamentStore.tournament.id)
    tournamentStore.setTournamentModalVisibility(true)
}

const isLateReg = computed(() => tournamentStore.getStageDetails(tournamentStore.tournament).type === 'Late Reg.')

watch(
    () => tournamentStore.tournament,
    (tournament) => {

        if (state.remainingTime > progress.value.timeRemaining) {
            state.remainingTime = progress.value.timeRemaining
        }

        if(tournament && state.remainingTime && !state.timerInterval) {
            startCountdown()
        }
    },
    {deep: true}
)

</script>

<template>
    <div
        id="tbrTournamentBar"
        class="show"
        v-if="tournamentStore.tournament"
        @click="handleTournamentBarClick"
    >
        <div
            :class="{
                tournamentBarComponent: true,
                notStarted: !progress.started
            }"
        >
            <div class="divPlayerPosition">
                {{ progress.position }}<span>/{{ progress.playerCount }}</span>
                <span class='fa fa-users' style="margin-left: 5px"></span>
            </div>
            <div
                :class="{
                    divTimeRemaining: true,
                    warning: state.isWarning
                }"
            >
                {{ formatSecondsToCountdownTime(state.remainingTime) }}
            </div>
            <div class="divPlayerPoints">
                {{ parseFloat(progress.points).toFixed(2) }}
            </div>
            <button
                class="btnStartTournament"
                @click="handleStartTournament"
            >
                START TOURNAMENT
            </button>
        </div>
    </div>

    <AlertModal
        v-if="state.isAlertVisible"
        confirm-text="CLOSE"
        :cancel-text="isLateReg ? 'REBUY' : 'PAYOUT'"
        @click:confirm="state.isAlertVisible = false"
        @click:cancel="() => isLateReg ? handleRebuyClick() : handlePayoutClick()"
    >
        Your {{ tournamentStore.tournament.buy_in_duration }}mins on
        <span class='spnGreen'>{{ tournamentStore.tournament.name }}</span>
        is now completed. You are currently placed in
        <span class='spnGreen'>{{ tournamentStore.getTournamentProgress.position }}</span> of
        {{ tournamentStore.getTournamentProgress.playerCount }} players.
    </AlertModal>

    <AlertModal
        v-if="state.isJoiningTournamentConfirmationVisible"
        cancel-text="No"
        confirm-text="Yes"
        @click:confirm="handleJoinTournament"
        @click:cancel="() => state.isJoiningTournamentConfirmationVisible = false"
    >
        Are you sure you would like to buy in to this tournament for
        <span class='spnGreen'>
            <CounterNumberValue
                :count="tournamentStore.tournament.buy_in"
                :decimal-places=2
                prefix="R"
            />
        </span>?
    </AlertModal>
</template>
